/**
 * `samples.*` wire contract (garment Increment 3, `garment.samples`).
 *
 * The single-round sampling tracker (V1 scope cut, 2026-07-19) + the PP gate.
 * Behind `entitledProcedureFor("garment.samples")`. Sample photos live in the
 * generic `attachments.*` service (`ownerType: "garment_sample_submission"`),
 * not here — a submission can carry several.
 */
import { z } from "zod";
import { uuid, isoDate } from "../_common.js";

export const roundStatus = z.enum(["pending", "submitted", "approved", "rejected"]);
const authorKind = z.enum(["buyer", "internal"]);

export const getForStyleInput = z.object({ styleId: uuid });

export const addSubmissionInput = z.object({
  styleId: uuid,
  notes: z.string().trim().max(2000).nullable().optional(),
  submittedAt: isoDate.nullable().optional(),
});
export const addSubmissionOutput = z.object({ id: uuid, revision: z.number().int().positive() });

export const addCommentInput = z.object({
  styleId: uuid,
  submissionId: uuid.nullable().optional(),
  body: z.string().trim().min(1).max(2000),
});
export const addCommentOutput = z.object({ id: uuid });

export const decideInput = z.object({
  submissionId: uuid,
  reason: z.string().trim().max(2000).nullable().optional(),
});
export const decideOutput = z.object({
  id: uuid,
  status: roundStatus,
  styleAdvancedToPpApproved: z.boolean(),
});

/** PP gate — the explicit meeting sign-off (see `approve-pp-gate.ts`). */
export const ppGateInput = z.object({ styleId: uuid });
export const approvePpGateInput = z.object({
  styleId: uuid,
  note: z.string().trim().max(2000).nullable().optional(),
});
export const approvePpGateOutput = z.object({ styleId: uuid, status: z.string() });

export const ppGateOutput = z.object({
  styleId: uuid,
  status: z.enum(["blocked", "ready", "approved"]),
  canApprove: z.boolean(),
  checks: z.array(
    z.object({
      key: z.string(),
      label: z.string(),
      met: z.boolean(),
      blocking: z.boolean(),
      detail: z.string(),
    }),
  ),
  approvedAt: z.string().nullable(),
  approvedByActorId: z.string().nullable(),
  note: z.string().nullable(),
  overridden: z.boolean(),
});

export const overrideInput = z.object({ styleId: uuid, reason: z.string().trim().min(1).max(2000) });
export const overrideOutput = z.object({ styleId: uuid });

/** Sourcing gate — sampling is blocked until materials are received/available. */
export const sourcingGateLine = z.object({
  bomLineId: uuid,
  label: z.string(),
  kind: z.enum(["fabric", "trim", "packing", "process"]),
  plan: z.enum(["to_source", "awaiting", "partly_ready", "ready", "issued"]),
});
export const sourcingGateInput = z.object({ styleId: uuid });
export const sourcingGateOutput = z.object({
  styleId: uuid,
  status: z.enum(["blocked", "ready"]),
  reason: z.string().nullable(),
  blockingLines: z.array(sourcingGateLine),
});

export const samplesView = z.object({
  styleId: uuid,
  ppApproved: z.boolean(),
  latestSubmissionId: uuid.nullable(),
  revision: z.number().int().nonnegative(),
  revisionCount: z.number().int().nonnegative(),
  status: roundStatus,
  submittedAt: isoDate.nullable(),
  notes: z.string().nullable(),
  commentCount: z.number().int().nonnegative(),
  comments: z.array(z.object({ authorKind, body: z.string(), at: z.string().datetime() })),
});
export const getForStyleOutput = samplesView;

/** The workspace-wide strike-file (PP) approval queue for the Floor/Station banner. */
export const pendingApprovalsOutput = z.object({
  items: z.array(
    z.object({
      styleId: uuid,
      styleRef: z.string(),
      name: z.string(),
      buyerName: z.string(),
      exFactoryDate: isoDate.nullable(),
      submissionId: uuid,
      revision: z.number().int().positive(),
      submittedAt: isoDate,
    }),
  ),
});

export type SamplesViewWire = z.infer<typeof samplesView>;
export type PendingApprovalsWire = z.infer<typeof pendingApprovalsOutput>;
